/**
 * Retry utilities for handling transient failures
 */

/**
 * Retry a function with linear backoff
 *
 * @param {Function} fn - The function to retry
 * @param {Object} options - Retry options
 * @returns {Promise} The result of the function
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    backoffStrategy = 'linear', // 'linear' or 'exponential'
    shouldRetry = () => true
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if shouldRetry returns false
      if (!shouldRetry(error)) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay
      const delay = backoffStrategy === 'exponential'
        ? baseDelay * Math.pow(2, attempt)
        : baseDelay * (attempt + 1);

      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`, error);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Determine if an error is retryable
 *
 * @param {Error} error - The error to check
 * @returns {boolean} True if error is retryable
 */
export function isRetryableError(error) {
  // Network errors
  if (!error.response) {
    return true;
  }

  // Server errors (5xx)
  if (error.response && error.response.status >= 500) {
    return true;
  }

  // Timeout errors
  if (error.code === 'ECONNABORTED') {
    return true;
  }

  // Rate limit errors (429)
  if (error.response && error.response.status === 429) {
    return true;
  }

  // Client errors (4xx) are not retryable
  return false;
}

/**
 * Create a retry configuration for common scenarios
 *
 * @param {string} scenario - The retry scenario ('network', 'api', 'custom')
 * @returns {Object} Retry configuration
 */
export function getRetryConfig(scenario = 'network') {
  const configs = {
    network: {
      maxRetries: 3,
      baseDelay: 1000,
      backoffStrategy: 'exponential',
      shouldRetry: isRetryableError
    },
    api: {
      maxRetries: 2,
      baseDelay: 1000,
      backoffStrategy: 'linear',
      shouldRetry: isRetryableError
    },
    custom: {
      maxRetries: 1,
      baseDelay: 500,
      backoffStrategy: 'linear',
      shouldRetry: () => true
    }
  };

  return configs[scenario] || configs.network;
}
